<template>
	<!--
	START: PRODUCT CARD
	-->
	<div
		:id="`product-card-${product.id}`"
		class="product-card relative border border-deepgray bg-white px-4 py-4 pt-0 md:px-10 md:pt-0 lg:pb-12 lg:pt-0"
	>
		<!-- Product Image	-->
		<div v-if="productImage">
			<img
				:src="productImage"
				:alt="productData.name || 'Product image'"
				class="relative -ml-2 h-[80px] md:h-[130px] lg:-ml-5 lg:h-[160px]"
			/>
		</div>

		<!-- Product Header -->
		<h3 class="lux-page-title mt-4 md:mt-4">
			{{ displayQuantity }}<span class="lowercase">x</span>
			{{ productData.name || "Product" }}
			<!-- <span class="text-black/40"> &mdash; x{{ displayQuantity }}</span> -->
		</h3>

		<!-- <div v-if="productImage" class="h-[80px] md:h-[110px] lg:h-[160px] absolute top-0 right-16 md:right-16">
			<img :src="productImage" :alt="productData.name || 'Product image'" class="w-full h-full object-contain" />
		</div> -->

		<!--
		Form Controls Section
		-->
		<div class="mb-0 flex flex-wrap gap-8 gap-y-2">
			<!-- Quantity Selector -->
			<InputWrap
				label="Quantity"
				:showLabel="true"
				:required="true"
				wrapClass="flex items-center"
			>
				<button
					type="button"
					@click="handleQuantityChange(-1)"
					class="lux-focus-ring flex h-9 w-9 items-center justify-center transition-colors hover:bg-gray-200"
					:disabled="localQuantity <= 1"
				>
					<PhMinus :size="16" weight="bold" />
				</button>
				<div class="flex flex-1 items-center justify-center">
					<input
						type="number"
						:value="localQuantity"
						@input="handleQuantityInput"
						min="1"
						class="lux-focus-ring w-full border-0 bg-transparent text-center text-base text-black"
					/>
				</div>
				<button
					type="button"
					@click="handleQuantityChange(1)"
					class="lux-focus-ring flex h-9 w-9 items-center justify-center transition-colors hover:bg-gray-200"
				>
					<PhPlus :size="16" weight="bold" />
				</button>
			</InputWrap>

			<!-- Size & Variant Selector Component -->
			<ProductSizeSelector :product="props.product" :context="'StepReport'" />

			<!-- Lumen Output Selector (only shown if product has lumen output options) -->
			<LumenOutputSelector
				:product="props.product"
				:selectedValue="props.product.selectedLumenOutput || ''"
				:showErrors="true"
				context="StepReport"
				@update:selectedValue="handleLumenOutputUpdate"
			/>

			<div class="min-w-[200px] flex-1"></div>
			<div class="min-w-[200px] flex-1"></div>
		</div>

		<!-- Acoustic Properties Section -->
		<div class="mt-4 flex flex-col gap-2">
			<h4 class="text-sm font-semibold uppercase leading-6 text-black"></h4>
			<div class="flex flex-col">
				<table
					class="reportTable lux-copy-normal w-full border-l border-t border-gray-200"
				>
					<tbody class="align-middle">
						<tr>
							<th>Sabins <span class="lowercase">(per unit)</span></th>
							<td>
								{{ acousticProps.sabinsPerUnit }} metric sabins
								<span class="lg:ml-2 text-black/25 block lg:inline-block">
									({{ acousticProps.sabinsPerUnitImperial }} imperial sabins)
								</span>
							</td>
						</tr>
						<tr>
							<th>
								Total Sabins (<span class="lowercase">x</span
								>{{ acousticProps.quantity }})
							</th>
							<td>
								{{ acousticProps.totalSabins }} metric sabins
								<span class="lg:ml-2 text-black/25 block lg:inline-block">
									({{ acousticProps.totalSabinsImperial }} imperial sabins)
								</span>
							</td>
						</tr>
						<tr>
							<th>Lumens <span class="lowercase">(per unit)</span></th>
							<td>{{ acousticProps.lumensPerUnit || 0 }} lm</td>
						</tr>
						<tr v-if="acousticProps.totalLumens != null">
							<th>
								Total Lumens (<span class="lowercase">x</span
								>{{ acousticProps.quantity }})
							</th>
							<td>{{ acousticProps.totalLumens }} lm</td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>

		<button
			v-if="!isPrimary"
			type="button"
			id="modal_close"
			@click="handleRemoveProduct"
			class="spin-effect lx-focus-ring absolute right-2 top-2 flex h-12 w-12 items-center justify-center text-gray-600 transition-colors hover:text-red-600"
		>
			<PhX
				size="31"
				aria-hidden="true"
				class="lux-pos-absmiddle text-blacka75 hover:text-red-600"
				title="Remove product"
			/>
		</button>
	</div>
	<!--
	END: PRODUCT CARD
	-->

	<!-- Remove Product Confirmation Dialog -->
	<BaseConfirmDialog
		:show="showRemoveConfirmation"
		title="Remove Product"
		:message="`Are you sure you want to remove <strong class='text-black'>${productName}</strong>?`"
		confirmLabel="Remove"
		@confirm="confirmRemoveProduct"
		@cancel="cancelRemoveProduct"
	/>
</template>

<script setup>
import { ref, computed, watch, onMounted, nextTick } from "vue";
import { useMainStore } from "@/stores/store.js";
import { Utils } from "@/utils.js";
import { PhMinus, PhPlus, PhX } from "@phosphor-icons/vue";
import ProductSizeSelector from "@/components/utils/ProductSizeSelector.vue";
import InputWrap from "@/components/utils/InputWrap.vue";
import LumenOutputSelector from "@/components/utils/LumenOutputSelector.vue";
import BaseConfirmDialog from "@/components/utils/BaseConfirmDialog.vue";

const store = useMainStore();

const props = defineProps({
	product: {
		type: Object,
		required: true,
	},
	isPrimary: {
		type: Boolean,
		default: false,
	},
	productIndex: {
		type: Number,
		required: true,
	},
});

// Local quantity value for debouncing
const localQuantity = ref(
	props.product.manualUnits ?? props.product.calculation?.unitsUsed ?? 1,
);

// Debounce timer
let debounceTimer = null;

// Watch for external changes to product
watch(
	() => props.product.manualUnits ?? props.product.calculation?.unitsUsed,
	(newValue) => {
		if (newValue !== undefined && newValue !== localQuantity.value) {
			localQuantity.value = newValue;
		}
	},
);

// Update extended data (lumen options) when size/variant changes
// This function is defined early so it can be called in onMounted
//
// RECURSION PREVENTION STRATEGY:
// This function calls store.updateProduct() which mutates the product and can trigger
// watchers in all ProductCard components, potentially causing infinite loops.
//
// Prevention mechanisms (multiple layers of defense):
// 1. Active Product Guard: Only updates if this product is the active product being edited
//    - If activeProductId is set: ONLY allow updates for that product
//    - If activeProductId is NOT set: only allow updates for first product (fallback)
//    This prevents watchers in other ProductCard components from mutating products they're not editing
//
// 2. Loading State Guard: Skips updates during initial data loading
//    Prevents updates from firing while App.vue is loading productsExtended data
//
// 3. Change Detection: Only updates if data has actually changed
//    Compares current dataExtended with new dataExtended before mutating
//    Prevents redundant mutations that would trigger watchers unnecessarily
//
// 4. Empty State Guard: Don't clear dataExtended if it's already empty
//    Prevents infinite loops when handleSizeChange already set dataExtended to []
//    and the watcher fires again trying to clear it
//
// NOTE: This function is primarily called from the watcher, but the watcher has its own
// guard to prevent calling this function when handleSizeChange already handled dataExtended.
const updateExtendedData = () => {
	$log.log(
		`[StepReport_ProductCard:updateExtendedData] Product ${props.product.id} called`,
	);

	// RECURSION PREVENTION: Skip update during initial data loading
	// Prevents updates from firing while App.vue is loading productsExtended
	if (store.overlays.loading) {
		$log.log(
			`[StepReport_ProductCard:updateExtendedData] Product ${props.product.id} skipping update - loading overlay active`,
		);
		return;
	}

	// RECURSION PREVENTION: Only update if this product is the active product being edited
	// This prevents updateExtendedData() from mutating products that aren't being edited,
	// which would trigger watchers in other ProductCard components
	if (store.user.activeProductId) {
		if (store.user.activeProductId !== props.product.id) {
			$log.log(
				`[StepReport_ProductCard:updateExtendedData] Product ${props.product.id} skipping update - not active product (active: ${store.user.activeProductId})`,
			);
			return;
		}
	} else {
		// If activeProductId is not set, only allow updates for first product (fallback behavior)
		const isFirstProduct =
			store.user.products &&
			store.user.products.length > 0 &&
			store.user.products[0]?.id === props.product.id;
		if (!isFirstProduct) {
			$log.log(
				`[StepReport_ProductCard:updateExtendedData] Product ${props.product.id} skipping update - not first product and no activeProductId set`,
			);
			return;
		}
	}

	let extendedData = null;

	// Check if productsExtended is available
	if (!store.productsExtended) {
		return;
	}

	if (!store.productsExtended[props.product.product]) {
		return;
	}

	const productExtendedData = store.productsExtended[props.product.product];

	// Check if this product actually has variants
	const hasVariants =
		productData.value.sizes?.variants?.all &&
		Object.keys(productData.value.sizes.variants.all).length > 0;

	if (hasVariants && props.product.size_variant) {
		// Get variant label from variantData computed property
		// If variantData is not available yet, try to get it from product.data
		let variantLabel = null;
		if (variantData.value && variantData.value.l) {
			variantLabel = variantData.value.l;
		} else if (
			productData.value.sizes?.variants?.all?.[props.product.size_variant]
		) {
			variantLabel =
				productData.value.sizes.variants.all[props.product.size_variant].l;
		}

		if (variantLabel) {
			// Find extended data for this variant
			// Match on size and variant label (e.g., "H2")
			extendedData = productExtendedData.filter((item) => {
				const sizeMatch = String(item.size) === String(props.product.size);
				const variantMatch = String(item.variant) === String(variantLabel);
				return sizeMatch && variantMatch;
			});
		}
	} else if (props.product.size) {
		// Find extended data for this size (no variant)
		// Match on size and empty variant string
		extendedData = productExtendedData.filter((item) => {
			const sizeMatch = String(item.size) === String(props.product.size);
			const variantMatch = String(item.variant) === "";
			return sizeMatch && variantMatch;
		});
	}

	// Note: NON-LIT options are added at data import time in data.js
	if (extendedData && extendedData.length > 0) {
		// RECURSION PREVENTION: Only update if the data has actually changed
		// This prevents redundant mutations that would trigger watchers unnecessarily
		const currentDataStr = JSON.stringify(props.product.dataExtended || []);
		const newDataStr = JSON.stringify(extendedData);

		if (currentDataStr !== newDataStr) {
			$log.log(
				`[StepReport_ProductCard:updateExtendedData] Product ${props.product.id} updating dataExtended - found ${extendedData.length} items`,
			);
			store.updateProduct(props.product.id, {
				dataExtended: extendedData,
				selectedLumenOutput:
					props.product.selectedLumenOutput || extendedData[0].output, // Keep existing selection or auto-select first option
			});
		} else {
			$log.log(
				`[StepReport_ProductCard:updateExtendedData] Product ${props.product.id} skipping update - dataExtended unchanged`,
			);
		}
	} else {
		// RECURSION PREVENTION: Don't update dataExtended if it's already empty
		// This prevents infinite loops when handleSizeChange already set dataExtended to []
		// and the watcher fires again trying to clear it
		if (props.product.dataExtended && props.product.dataExtended.length > 0) {
			// dataExtended exists but doesn't match current size/variant - clear it
			$log.log(
				`[StepReport_ProductCard:updateExtendedData] Product ${props.product.id} clearing dataExtended - no extended data found for current size/variant`,
			);
			store.updateProduct(props.product.id, {
				dataExtended: [],
				selectedLumenOutput: "",
			});
		} else {
			// Already empty - skip update to prevent recursion
			$log.log(
				`[StepReport_ProductCard:updateExtendedData] Product ${props.product.id} skipping clear - dataExtended already empty (likely set by handleSizeChange)`,
			);
		}
	}
};

// Ensure dataExtended is populated when component mounts if product has size but no dataExtended
onMounted(() => {
	// If product has a size selected but no dataExtended, populate it
	if (
		props.product.size &&
		(!props.product.dataExtended || props.product.dataExtended.length === 0)
	) {
		updateExtendedData();
	}
});

// Watch for size/variant changes to ensure dataExtended is updated
//
// RECURSION PREVENTION STRATEGY:
// This watcher monitors size/variant changes and calls updateExtendedData() to populate
// extended product data. However, updateExtendedData() calls store.updateProduct() which
// mutates the product and can trigger this watcher again, creating infinite loops.
//
// The Problem:
// When handleSizeChange() mutates a product, it triggers watchers in ALL ProductCard components.
// Without guards, each watcher would call updateExtendedData(), which mutates the product again,
// triggering more watchers → infinite loop.
//
// Prevention mechanisms (multiple guards):
// 1. Active Product Guard: Only allow updates if this product is the active product being edited
//    - If activeProductId is set: ONLY allow updates for that product
//    - If activeProductId is NOT set: only allow updates for first product (matches updateCurrentProduct fallback)
//    This prevents watchers in other ProductCard components from firing when ProductSizeSelector
//    updates a different product. Without this, ALL ProductCard watchers would fire simultaneously.
//
// 2. DataExtended Already Handled Guard: Skip updateExtendedData() if handleSizeChange already handled it
//    - handleSizeChange() ALWAYS sets dataExtended (either with data or as empty array [])
//    - If dataExtended is already defined (not null/undefined), handleSizeChange already handled it
//    - Only call updateExtendedData() if dataExtended is missing entirely
//    This prevents double mutations that would trigger recursion.
//
// 3. Flush Timing: Uses flush: 'post' to delay callback until after DOM updates complete
//    Additional safety layer to prevent recursion during synchronous mutation cycles.
//    Ensures watcher runs after all batched updates from handleSizeChange are complete.
//
// 4. updateExtendedData() has its own guards: Additional safety checks inside the function
//    to prevent mutations when it's not the active product or during loading states.
//
// Flow Example (with 2 products):
// - User selects size for Product A
// - handleSizeChange() batches updates → single mutation → sets dataExtended: []
// - ProductCard A watcher fires → Guard 1 passes (is active product) → Guard 2 blocks (dataExtended already set)
// - ProductCard B watcher fires → Guard 1 blocks (not active product)
// - No recursion!
watch(
	() => [props.product.size, props.product.size_variant],
	(newValues, oldValues) => {
		$log.log(
			`[StepReport_ProductCard:watch] Product ${props.product.id} watcher fired - size: ${newValues[0]}, variant: ${newValues[1]}`,
		);

		if (!props.product.size) {
			$log.log(
				`[StepReport_ProductCard:watch] Product ${props.product.id} watcher skipped - no size`,
			);
			return;
		}

		// RECURSION PREVENTION: Only update if this product is the active product being edited
		// If activeProductId is set, ONLY allow updates for that product
		// If activeProductId is NOT set, only allow updates for the first product (fallback behavior)
		// This prevents watchers in other ProductCard components from firing
		// when ProductSizeSelector updates a different product
		if (store.user.activeProductId) {
			// activeProductId is set - only allow updates for that product
			if (store.user.activeProductId !== props.product.id) {
				$log.log(
					`[StepReport_ProductCard:watch] Product ${props.product.id} watcher blocked - not active product (active: ${store.user.activeProductId})`,
				);
				return;
			}
		} else {
			// activeProductId is NOT set - only allow updates for first product (index 0)
			// This matches the fallback behavior in updateCurrentProduct
			const isFirstProduct =
				store.user.products &&
				store.user.products.length > 0 &&
				store.user.products[0]?.id === props.product.id;
			if (!isFirstProduct) {
				$log.log(
					`[StepReport_ProductCard:watch] Product ${props.product.id} watcher blocked - not first product and no activeProductId`,
				);
				return;
			}
		}

		// RECURSION PREVENTION: Skip updateExtendedData() if handleSizeChange already handled dataExtended
		//
		// CRITICAL: handleSizeChange() in ProductSizeSelector.vue ALWAYS sets dataExtended as part of
		// its batched update (either with found data or as empty array []). This means if dataExtended
		// is already defined (not null/undefined), handleSizeChange has already handled it.
		//
		// We should ONLY call updateExtendedData() if dataExtended is missing entirely (null/undefined),
		// which happens when:
		// - Product is first added without size selection
		// - Component mounts before handleSizeChange runs
		// - Product dataExtended was never initialized
		//
		// This prevents the infinite loop scenario:
		// 1. User selects size → handleSizeChange() sets dataExtended: [] (no extended data found)
		// 2. Product mutation triggers watcher (size changed)
		// 3. Watcher calls updateExtendedData() → tries to clear dataExtended again
		// 4. updateExtendedData() calls store.updateProduct() → mutates product
		// 5. Mutation triggers watcher again → infinite loop
		//
		// By checking if dataExtended is already set, we prevent step 3 from happening,
		// breaking the recursion cycle.
		if (
			props.product.dataExtended !== undefined &&
			props.product.dataExtended !== null
		) {
			// dataExtended is already set (either with data or empty array)
			// This means handleSizeChange already handled it, so skip updateExtendedData()
			$log.log(
				`[StepReport_ProductCard:watch] Product ${props.product.id} watcher skipped - dataExtended already set (likely by handleSizeChange)`,
			);
			return;
		}

		$log.log(
			`[StepReport_ProductCard:watch] Product ${props.product.id} watcher passed guard - calling updateExtendedData()`,
		);
		updateExtendedData();
	},
	{
		immediate: false,
		flush: "post", // Additional safety: delays callback until after DOM updates
	},
);

// Get product details from catalog
const productData = computed(() => {
	return props.product.data || {};
});

// Get size data
const sizeData = computed(() => {
	if (!productData.value.sizes?.items) return null;
	return productData.value.sizes.items[props.product.size];
});

// Get variant data
const variantData = computed(() => {
	if (!props.product.size_variant || !productData.value.sizes?.variants?.all)
		return null;
	return productData.value.sizes.variants.all[props.product.size_variant];
});

// Get dimensions with unit conversion
const dimensions = computed(() => {
	const unit = store.user.room.unit;
	const width = sizeData.value?.w || 0;
	const height = variantData.value?.h || sizeData.value?.h || 0;
	const depth = sizeData.value?.d || 0;

	return {
		width: width,
		height: height,
		depth: depth,
		unit: unit === "imperial" ? "in" : "mm",
	};
});

// Convert metric sabins to imperial sabins
const convertMetricToImperialSabins = (metricSabins) => {
	const conversionFactor = 10.7639;
	return (parseFloat(metricSabins) * conversionFactor).toFixed(2);
};

// Get acoustic properties
const acousticProps = computed(() => {
	const sabinsPerUnit = parseFloat(props.product.metric_sabins || 0);
	// Use localQuantity for immediate UI updates (it syncs with manualUnits/calculation via watcher)
	const actualQuantity = localQuantity.value;
	const totalSabins = sabinsPerUnit * actualQuantity;

	// Lumen calculations
	// selectedLumenOutput stores the output code (e.g., "3200"), not the lumen value
	// We need to look up the actual lumen_total from dataExtended
	let lumensPerUnit = 0;
	if (props.product.selectedLumenOutput && props.product.dataExtended) {
		const selectedOption = props.product.dataExtended.find(
			(item) => item.output === props.product.selectedLumenOutput,
		);
		if (selectedOption && selectedOption.lumen_total) {
			lumensPerUnit = parseFloat(selectedOption.lumen_total);
		}
	}
	const totalLumens = lumensPerUnit * actualQuantity;

	return {
		sabinsPerUnit: sabinsPerUnit.toFixed(2),
		sabinsPerUnitImperial: convertMetricToImperialSabins(sabinsPerUnit),
		totalSabins: totalSabins.toFixed(2),
		totalSabinsImperial: convertMetricToImperialSabins(totalSabins),
		quantity: actualQuantity,
		lumensPerUnit: lumensPerUnit > 0 ? lumensPerUnit.toLocaleString() : null,
		totalLumens: totalLumens > 0 ? totalLumens.toLocaleString() : null,
	};
});

// Display quantity (calculated vs manual)
const displayQuantity = computed(() => {
	return localQuantity.value;
});

// Handle quantity change with debouncing
const handleQuantityChange = (delta) => {
	const newQuantity = Math.max(1, localQuantity.value + delta);
	localQuantity.value = newQuantity;

	// Clear existing timer
	if (debounceTimer) {
		clearTimeout(debounceTimer);
	}

	// Set new debounce timer (500ms)
	debounceTimer = setTimeout(() => {
		store.updateProductManualUnits(props.product.id, newQuantity);
	}, 500);
};

// Handle direct input change
const handleQuantityInput = (event) => {
	const newQuantity = Math.max(1, parseInt(event.target.value) || 1);
	localQuantity.value = newQuantity;

	// Clear existing timer
	if (debounceTimer) {
		clearTimeout(debounceTimer);
	}

	// Set new debounce timer (500ms)
	debounceTimer = setTimeout(() => {
		store.updateProductManualUnits(props.product.id, newQuantity);
	}, 500);
};

// Update metric sabins based on size/variant selection
const updateMetricSabins = () => {
	let newSabins = "0";

	if (props.product.size_variant && variantData.value) {
		newSabins = variantData.value.metric_sabins;
	} else if (sizeData.value) {
		newSabins = sizeData.value.metric_sabins;
	}

	store.updateProduct(props.product.id, { metric_sabins: newSabins });

	// Don't call calculateProductsBatch here - let the watcher in RoomCanvas handle it
	// This prevents duplicate calls and recursion issues
};

// Note: updateExtendedData is now defined earlier in the file (before onMounted)

// Confirmation state for product removal
const showRemoveConfirmation = ref(false);

// Handle remove product (with confirmation)
const handleRemoveProduct = () => {
	if (!props.isPrimary) {
		showRemoveConfirmation.value = true;
	}
};

// Confirm removal
const confirmRemoveProduct = () => {
	if (!props.isPrimary) {
		store.removeProduct(props.product.id);
		// Trigger recalculation for remaining products
		store.calculateProductsBatch();
	}
	showRemoveConfirmation.value = false;
};

// Cancel removal
const cancelRemoveProduct = () => {
	showRemoveConfirmation.value = false;
};

// Get product name for confirmation message
const productName = computed(() => {
	return productData.value.name || `Product ${props.product.product}`;
});

// Get product image
const productImage = computed(() => {
	// Try shape-aware hero image first, then cover image
	return (
		Utils.resolveHeroImage(
			productData.value.images?.hero,
			props.product.shape,
		) ||
		productData.value.images?.cover?.[0] ||
		""
	);
});

// Get size options
// Note: Sizes are already filtered at import time based on "Import?" column
const sizeOptions = computed(() => {
	if (!productData.value.sizes?.items) return {};
	return productData.value.sizes.items;
});

// Get variant options for selected size
const variantOptions = computed(() => {
	if (!props.product.size || !productData.value.sizes?.variants?.all) return {};

	const result = {};
	Object.entries(productData.value.sizes.variants.all).forEach(
		([key, value]) => {
			if (value.size === props.product.size) {
				result[key] = value;
			}
		},
	);
	return result;
});

// Check if product has variants
const hasVariants = computed(() => {
	return Object.keys(variantOptions.value).length > 0;
});

// Handle lumen output update from LumenOutputSelector component
const handleLumenOutputUpdate = (newValue) => {
	store.updateProduct(props.product.id, { selectedLumenOutput: newValue });
};
</script>

<style scoped>
.product-card button:disabled {
	opacity: 0.5;
	cursor: not-allowed;
}

/* Highlight effect when scrolled to */
.product-card.highlight-product {
	animation: highlightPulse 2s ease-in-out;
}

@keyframes highlightPulse {
	0% {
		background-color: rgba(255, 255, 0, 0.2);
	}

	50% {
		background-color: rgba(255, 255, 0, 0.4);
	}

	100% {
		background-color: transparent;
	}
}
</style>
